import { defineStore } from "pinia";
import type {
  AuditLogStatusResponse,
  EmailSecurityReportResponse,
} from "shared-core/types/microsoft";
import type {
  MicrosoftStaleUserResponse,
  StaleUserActionRequest,
  StaleUserApplyResponse,
  SuperAdminCountResponse,
} from "shared-core/types/user";
import { buildUserFullName } from "shared-core/utils/all-utils";
import type { SuperAdminsAwareInterface } from "shared-ui/types/integration-users-store.ts";
import type { MicrosoftIntegrationStore } from "shared-ui/types/microsoft-integration-store.ts";
import { useApi } from "../utils/api.ts";

export const useMicrosoft365UsersStore = defineStore("microsoft365Users", {
  state: () => ({
    stale: {
      users: null,
      reasons: [],
      lastUpdated: null,
    },
    superAdminCount: null,
  }),
  actions: {
    async fetchStaleUsers(): Promise<void> {
      const response = await useApi().get<MicrosoftStaleUserResponse>(
        "integrations/microsoft-365/stale-users",
      );
      const responseData = await response.data;
      this.stale.users =
        responseData.staleUsers !== null
          ? responseData.staleUsers.map((user) => ({
              ...user,
              fullName: buildUserFullName(user),
            }))
          : null;
      this.stale.lastUpdated = responseData.lastUpdated;
    },

    async refreshStaleUsers(): Promise<void> {
      const response = await useApi().post<MicrosoftStaleUserResponse>(
        "integrations/microsoft-365/stale-users/refresh",
      );
      const responseData = await response.data;
      this.stale.users =
        responseData.staleUsers !== null
          ? responseData.staleUsers.map((user) => ({
              ...user,
              fullName: buildUserFullName(user),
            }))
          : null;
      this.stale.lastUpdated = responseData.lastUpdated;
    },

    async applyStaleUserActions(
      actions: StaleUserActionRequest[],
    ): Promise<StaleUserApplyResponse> {
      const response = await useApi().post<StaleUserApplyResponse>(
        "integrations/microsoft-365/stale-users/apply",
        {
          json: { actions },
        },
      );
      return response.data;
    },

    async fetchSuperAdminCount(): Promise<void> {
      const response = await useApi().get<SuperAdminCountResponse>(
        "integrations/microsoft-365/super-admin-count",
      );
      this.superAdminCount = (await response.data).count;
    },

    async getMicrosoftEmailProtectionReport(): Promise<EmailSecurityReportResponse> {
      const response = await useApi().get<EmailSecurityReportResponse>(
        "integrations/microsoft-365/email-protection-report",
      );
      return response.data;
    },

    async triggerEmailProtectionReport(): Promise<EmailSecurityReportResponse> {
      const response = await useApi().post<EmailSecurityReportResponse>(
        "integrations/microsoft-365/trigger-email-protection-report",
      );
      return response.data;
    },

    async updateMicrosoftAuditLogStatus(): Promise<AuditLogStatusResponse> {
      const response = await useApi().post<AuditLogStatusResponse>(
        "integrations/microsoft-365/trigger-audit-log-update",
      );
      return response.data;
    },
  } satisfies ThisType<SuperAdminsAwareInterface & MicrosoftIntegrationStore>,
  getters: {},
});
